import { useMy } from '@/hooks/useMy';
import { NodeDetails, Operator, Webpage } from '@/types';

/**
 * Maps UI sort keys to backend Neo4j property names when they differ —
 * webpages-local mirror of the Assets pattern (asset/config/columns.ts
 * SORT_KEY_TO_BACKEND), kept separate so asset-only mappings never leak
 * into the webpage query.
 */
const WEBPAGE_SORT_KEY_TO_BACKEND: Record<string, string> = {
  riskScore: 'risk_score',
};

interface UseWebpagesProps {
  search: string;
  orderBy: keyof Webpage;
  descending: boolean;
  typeFilter: string[];
  serviceFilter: string[];
}

export function useWebpages({
  search,
  orderBy,
  descending,
  typeFilter,
  serviceFilter,
}: UseWebpagesProps) {
  const nodeFilters: NonNullable<NodeDetails['filters']> = [];

  if (search) {
    nodeFilters.push({
      field: 'url',
      operator: 'CONTAINS' as Operator,
      value: search,
    });
  }

  if (typeFilter.length === 1) {
    nodeFilters.push({
      field: 'type',
      operator: '=' as Operator,
      value: typeFilter[0],
    });
  }

  if (serviceFilter.length === 1) {
    nodeFilters.push({
      field: 'service',
      operator: '=' as Operator,
      value: serviceFilter[0],
    });
  }

  // The ONLY network path (ENG-4395): the scoring cron stamps `risk_score`
  // onto the Webpage node itself, so `webpage.riskScore` arrives on this
  // query — the old second `#webapplication#` asset query (≤50 round-trips
  // building an origin→score map) is retired.
  return useMy(
    {
      resource: 'webpage',
      query: {
        limit: 100,
        orderBy: WEBPAGE_SORT_KEY_TO_BACKEND[orderBy] ?? orderBy,
        descending,
        node: {
          labels: ['Webpage'],
          filters: nodeFilters,
        },
      },
    },
    // CONSTRAINT: keyset pagination is required here, not an optimization.
    // The legacy offset /my path appends an `orderBy IS NOT NULL` hint filter
    // server-side, which would silently hide every never-scored webpage under
    // the risk_score sort; the cursor path skips the hint and sorts NULL rows
    // last (ENG-4395 contract).
    { cursorPagination: true }
  );
}
